// Framework dependencies
import { useCallback } from 'react';
import { parseNormalizationResponse } from '@updater/ui-widgets';

// Global dependencies
import { useRESTClient } from 'context';
import { formatRESTAddress, formatToRESTAddress } from 'utils/addresses';
import { ItemKinds } from 'types';

// Local dependencies
import { ItemStatuses } from '../../../constants';
import { ForwardMailFlow, SubmittedFormProps } from '../types';
import { dateWithoutTimestamp, dateWithNeutralTimestamp } from './dates';

/**
 * @method formatFormDataToSend
 * @description format ForwardMailFlow data into axios data
 * @param {string} nameId - user name id
 * @param {ForwardMailFlow} data - FYM flow store data
 * @returns formatted data valid for API request
 */
export const formatFormDataToSend = (nameId: string, data: ForwardMailFlow) => {
  if (data.status === ItemStatuses.CANCELLED) {
    return { status: data.status };
  }

  if (!data.currentAddress || !data.newAddress) {
    return;
  }

  const currentAddressSmartyStreetsResponse = parseNormalizationResponse(
    data.currentAddress?.normalizationResponse ?? '',
    true
  );
  const newAddressSmartyStreetsResponse = parseNormalizationResponse(
    data.newAddress?.normalizationResponse ?? '',
    true
  );
  return {
    form_type: data.whosMoving,
    from_address: {
      ...currentAddressSmartyStreetsResponse,
      ...formatToRESTAddress(
        data.currentAddress,
        currentAddressSmartyStreetsResponse.status
      ),
    },
    to_address: {
      ...newAddressSmartyStreetsResponse,
      ...formatToRESTAddress(data.newAddress),
    },
    start_date: dateWithoutTimestamp(data.forwardMailDate),
    end_date:
      data.isMoveTemporary === 'yes' && data.stopForwardMailDate
        ? dateWithoutTimestamp(data.stopForwardMailDate)
        : '',
    move_id: parseInt(data.moveId, 10),
    kind: ItemKinds.FORWARD_MAIL,
    name_id: parseInt(nameId, 10),
    business_name: data.businessName,
    created_at: new Date().toISOString(),
  };
};

/**
 * @method formatFormDataToStore
 * @description format axios data into aForwardMailFlow data
 * @param {SubmittedFormProps} data - axios data
 * @returns {ForwardMailFlow} formatted store data
 */
export const formatFormDataToStore = (
  data: SubmittedFormProps
): Partial<ForwardMailFlow> => {
  return {
    ...data,
    confirmAddresses: 'viewed',
    whosMoving: data.form_type,
    currentAddress: formatRESTAddress(data.from_address),
    newAddress: formatRESTAddress(data.to_address),
    forwardMailDate: dateWithNeutralTimestamp(data.start_date),
    // @ts-expect-error FIXME: TS2322 - strict mode migration
    stopForwardMailDate: dateWithNeutralTimestamp(data.end_date) || null,
    moveId: data.move_id.toString(),
    nameId: data.name_id.toString(),
    businessName: data.business_name,
    isMoveTemporary: data.end_date ? 'yes' : 'no',
    firstName: data.name.first,
    lastName: data.name.last,
    middleName: data.name.middle,
    suffixName: data.name.suffix,
    status: data.status,
    id: data.id.toString(),
  };
};

/**
 * @method createUserNameID
 * @description Create new user request function.
 * @returns function that run the axios request who will return user name that contains name_id
 * params that are needed for returned function are
 * @param {string} firstName
 * @param {string} lastName
 */
export const createUserNameID = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { axios } = useRESTClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getResults = useCallback(
    // @ts-expect-error FIXME: TS7031 - strict mode migration
    ({ firstName, lastName, suffixName, middleName }) => {
      return axios
        .post('/v2/names', {
          first: firstName,
          last: lastName,
          suffix: suffixName,
          middle: middleName,
        })
        .then((res: { data: any }) => res.data);
    },
    [axios]
  );

  return {
    getResults,
  };
};

/**
 * @method patchUserNameId
 * @description Update new user request function.
 * @returns function that run the axios request who will update the name of the user with new values
 * params that are needed for returned function are
 * @param {number} id - name_id of current username
 * @param {string} firstName
 * @param {string} lastName
 */
export const patchUserNameId = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { axios } = useRESTClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getResults = useCallback(
    // @ts-expect-error FIXME: TS7031 - strict mode migration
    (id: number, { firstName, lastName, suffixName, middleName }) => {
      return axios
        .patch(`/v2/names/${id}`, {
          first: firstName,
          last: lastName,
          suffix: suffixName,
          middle: middleName,
        })
        .then((res: { data: any }) => res.data);
    },
    [axios]
  );

  return {
    getResults,
  };
};

/**
 * @method submitForwardMail
 * @description Submit form request function.
 * @returns function that run the axios request who will submit the new form
 * params that are needed for returned function are
 * @param {string} nameId - user name id
 * @param {ForwardMailFlow} data
 */
export const submitForwardMail = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { axios } = useRESTClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getResults = useCallback(
    (nameId: string, data: ForwardMailFlow) => {
      return axios
        .post('/v2/items', formatFormDataToSend(nameId, data))
        .then((res: { data: any }) => res.data);
    },
    [axios]
  );

  return {
    getResults,
  };
};

/**
 * @method patchForwardMail
 * @description Update form request function.
 * @returns function that run the axios request who will update the existing form
 * params that are needed for returned function are
 * @param {number} id - existing form id
 * @param {string} nameId - user name id
 * @param {ForwardMailFlow} data
 */
export const patchForwardMail = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { axios } = useRESTClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getResults = useCallback(
    (id: string, nameId: string, data: ForwardMailFlow) => {
      return axios
        .patch(`/v2/items/${id}`, formatFormDataToSend(nameId, data))
        .then((res: { data: any }) => res.data);
    },
    [axios]
  );

  return {
    getResults,
  };
};

/**
 * @method fetchForwardMails
 * @description fetch forms request function.
 * @returns function that run the axios request who will fetch created forms
 */
export const fetchForwardMails = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { axios } = useRESTClient();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getResults = useCallback(
    () =>
      axios
        .get('/v2/items')
        .then((res: { data: any }) => res.data)
        .then((data: any[]) =>
          data.filter(
            (form: { type: string }) => form.type === ItemKinds.FORWARD_MAIL
          )
        ),
    [axios]
  );

  return {
    getResults,
  };
};
